import { Card } from '@/components/ui';
import { Euro, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StockValueWidgetProps {
  totalValue: number;
  previousValue?: number;
  currency?: string;
}

export function StockValueWidget({ totalValue, previousValue, currency = '€' }: StockValueWidgetProps) {
  const formatValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toFixed(0);
  };

  const getTrend = () => {
    if (previousValue === undefined || previousValue === 0) return null;

    const diff = totalValue - previousValue;
    const percent = Math.round((diff / previousValue) * 100);

    if (diff === 0) {
      return { icon: <Minus size={14} />, text: '0%', color: 'text-gray-500' };
    }

    return diff > 0
      ? { icon: <TrendingUp size={14} />, text: `+${percent}%`, color: 'text-green-600' }
      : { icon: <TrendingDown size={14} />, text: `${percent}%`, color: 'text-red-600' };
  };

  const trend = getTrend();

  return (
    <Card
      padding="md"
      className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-8 -top-8 w-32 h-32 bg-white rounded-full"></div>
        <div className="absolute -left-4 -bottom-4 w-20 h-20 bg-white rounded-full"></div>
      </div>

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white/20 rounded-lg">
              <Euro size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-sm sm:text-base">Valeur du stock</h3>
              <p className="text-xs text-emerald-100">Estimation actuelle</p>
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl sm:text-4xl font-bold">
              {formatValue(totalValue)}
              <span className="text-lg ml-1">{currency}</span>
            </p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 ${trend.color} bg-white/20 rounded-full px-2 py-0.5 w-fit`}>
                {trend.icon}
                <span className="text-xs font-medium text-white">{trend.text} vs semaine dernière</span>
              </div>
            )}
          </div>

          <div className="text-right text-xs text-emerald-100">
            <p>Basé sur les</p>
            <p>prix unitaires</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
